import { useState, useEffect, useMemo, useRef } from 'react';
import { getConnectorConfig, saveConnectorConfigs } from '../utils/connectorConfig';
import {
  PackagePlus,
  Settings,
  Save,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  Clock,
  Plus,
  Trash2,
  X,
  Eye,
  EyeOff,
  Search,
  ChevronLeft,
  ChevronRight,
  Shield,
  Database,
  FileSignature,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import axios from 'axios';

const PAGE_SIZE = 10;

// ── Searchable combobox ──────────────────────────────────────────────────────
function Combobox({ options, value, onChange, placeholder, emptyText }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = options.filter(o =>
    o.label.toLowerCase().includes(query.toLowerCase()) ||
    o.value.toLowerCase().includes(query.toLowerCase())
  );

  const selectedLabel = options.find(o => o.value === value)?.label || '';

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={open ? query : selectedLabel}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => { setOpen(true); setQuery(''); }}
          placeholder={placeholder}
          className="w-full pl-8 pr-8 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-gray-100 placeholder-gray-400"
        />
        {value && (
          <button type="button" onClick={() => { onChange(''); setQuery(''); setOpen(false); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
            <X size={14} />
          </button>
        )}
      </div>
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl max-h-52 overflow-y-auto combobox-scroll">
          {filtered.length === 0 ? (
            <p className="px-4 py-3 text-sm text-gray-400 italic">{emptyText || 'No matches'}</p>
          ) : (
            filtered.map(opt => (
              <button key={opt.value} type="button"
                onClick={() => { onChange(opt.value); setQuery(''); setOpen(false); }}
                className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-700 transition-colors ${opt.value === value ? 'bg-blue-600/20 text-blue-300' : 'text-gray-100'}`}>
                <div className="font-medium truncate">{opt.label}</div>
                {opt.sublabel && <div className="text-xs text-gray-400 truncate">{opt.sublabel}</div>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default function DataOffers() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [providerUrl, setProviderUrl] = useState(
    getConnectorConfig('policiesProviderUrl') || getConnectorConfig('assetsProviderUrl')
  );
  const [apiKey, setApiKey] = useState(
    getConnectorConfig('policiesApiKey') || getConnectorConfig('assetsApiKey')
  );
  const [showConfig, setShowConfig] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [apiStatus, setApiStatus] = useState('checking');
  const [apiError, setApiError] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Modal state
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');
  const [policies, setPolicies] = useState([]);
  const [assets, setAssets] = useState([]);
  const [newOffer, setNewOffer] = useState({
    id: '',
    accessPolicyId: '',
    contractPolicyId: '',
    assetId: '',
  });

  useEffect(() => {
    if (providerUrl) fetchOffers();
    else setLoading(false);
  }, [providerUrl]);

  useEffect(() => { setCurrentPage(1); }, [searchQuery]);

  const edcDelete = async (path) => {
    const token = localStorage.getItem('kc_token');
    const isExternal = providerUrl.startsWith('http://') ||
                       !providerUrl.startsWith('https://enact-dataspace.iti.gr');
    if (isExternal) {
      await axios.post('/api/v1/edc-proxy', {
        targetUrl: providerUrl,
        path,
        method: 'DELETE',
        headers: apiKey ? { 'X-Api-Key': apiKey } : {}
      }, { headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
    } else {
      await axios.delete(`${providerUrl}${path}`, {
        headers: { 'Content-Type': 'application/json', ...(apiKey ? { 'X-Api-Key': apiKey } : {}) }
      });
    }
  };

  // ── Fetch contract definitions ───────────────────────────────────────────────
  const fetchOffers = async () => {
    if (!providerUrl) { setLoading(false); return; }
    setLoading(true);
    setApiStatus('checking');
    setApiError('');
    try {
      const data = await fetchEndpoint('/api/management/v2/contractdefinitions/request');
      setOffers(data);
      setApiStatus('connected');
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to load data offers.';
      setApiError(msg);
      setApiStatus('error');
      setOffers([]);
    } finally {
      setLoading(false);
    }
  };

  const saveConfiguration = () => {
    saveConnectorConfigs({
      policiesProviderUrl: providerUrl, policiesApiKey: apiKey,
      assetsProviderUrl: providerUrl, assetsApiKey: apiKey
    });
    setShowConfig(false);
    fetchOffers();
  };

  // ── Fetch a single endpoint (same approach as Policies page) ────────────────
  const fetchEndpoint = async (path) => {
    const isExternal = providerUrl.startsWith('http://') || !providerUrl.startsWith('https://enact-dataspace.iti.gr');
    const token = localStorage.getItem('kc_token');
    const body = { '@context': {}, limit: 1000, offset: 0 };
    if (isExternal) {
      const res = await axios.post('/api/v1/edc-proxy', {
        targetUrl: providerUrl,
        path,
        method: 'POST',
        data: body,
        headers: apiKey ? { 'X-Api-Key': apiKey } : {}
      }, { headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
      return Array.isArray(res.data) ? res.data : [];
    }
    const res = await axios.post(`${providerUrl}${path}`, body, {
      headers: { 'Content-Type': 'application/json', ...(apiKey ? { 'X-Api-Key': apiKey } : {}) }
    });
    return Array.isArray(res.data) ? res.data : [];
  };

  const openCreateModal = async () => {
    setModalError('');
    setNewOffer({ id: '', accessPolicyId: '', contractPolicyId: '', assetId: '' });
    setPolicies([]);
    setAssets([]);
    setShowCreateModal(true);
    setModalLoading(true);
    try {
      const [polData, assetData] = await Promise.all([
        fetchEndpoint('/api/management/v2/policydefinitions/request'),
        fetchEndpoint('/api/management/v2/assets/request'),
      ]);
      setPolicies(polData);
      setAssets(assetData);
    } catch (err) {
      setModalError(err.response?.data?.message || err.message || 'Failed to load data.');
    } finally {
      setModalLoading(false);
    }
  };

  // ── Create contract definition ───────────────────────────────────────────────
  const createOffer = async () => {
    if (!newOffer.id || !newOffer.accessPolicyId || !newOffer.contractPolicyId || !newOffer.assetId) {
      setModalError('Please fill in all required fields.');
      return;
    }
    setModalLoading(true);
    setModalError('');
    try {
      const token = localStorage.getItem('kc_token');
      const isExternal = providerUrl.startsWith('http://') ||
                         !providerUrl.startsWith('https://enact-dataspace.iti.gr');
      const payload = {
        '@context': { '@vocab': 'https://w3id.org/edc/v0.0.1/ns/' },
        '@id': newOffer.id,
        accessPolicyId: newOffer.accessPolicyId,
        contractPolicyId: newOffer.contractPolicyId,
        assetsSelector: [
          {
            operandLeft: 'https://w3id.org/edc/v0.0.1/ns/id',
            operator: '=',
            operandRight: newOffer.assetId,
          }
        ]
      };
      if (isExternal) {
        await axios.post('/api/v1/edc-proxy', {
          targetUrl: providerUrl,
          path: '/api/management/v2/contractdefinitions',
          method: 'POST',
          data: payload,
          headers: apiKey ? { 'X-Api-Key': apiKey } : {}
        }, { headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
      } else {
        await axios.post(`${providerUrl}/api/management/v2/contractdefinitions`, payload, {
          headers: { 'Content-Type': 'application/json', ...(apiKey ? { 'X-Api-Key': apiKey } : {}) }
        });
      }
      setShowCreateModal(false);
      fetchOffers();
    } catch (err) {
      setModalError(err.response?.data?.message || err.message || 'Failed to create data offer.');
    } finally {
      setModalLoading(false);
    }
  };

  // ── Delete contract definition ───────────────────────────────────────────────
  const deleteOffer = async (id) => {
    if (!confirm(`Delete data offer "${id}"?`)) return;
    try {
      await edcDelete(`/api/management/v2/contractdefinitions/${encodeURIComponent(id)}`);
      fetchOffers();
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to delete data offer.');
    }
  };

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const getOfferProp = (offer, ...keys) => {
    for (const k of keys) {
      if (offer[k] !== undefined && offer[k] !== null) return offer[k];
    }
    return undefined;
  };

  const getOfferId = (offer) => offer['@id'] || offer.id || '—';
  const getAccessPolicy = (offer) => getOfferProp(offer, 'edc:accessPolicyId', 'accessPolicyId') || '—';
  const getContractPolicy = (offer) => getOfferProp(offer, 'edc:contractPolicyId', 'contractPolicyId') || '—';
  const getAssetSelector = (offer) => {
    const sel = getOfferProp(offer, 'edc:assetsSelector', 'assetsSelector');
    if (!sel) return '—';
    const first = Array.isArray(sel) ? sel[0] : sel;
    if (!first) return '—';
    return getOfferProp(first, 'edc:operandRight', 'operandRight') || JSON.stringify(first);
  };

  const getPolicyId = (p) => p['@id'] || p.id || '';
  const getAssetId = (a) => a['@id'] || a.id || '';
  const getAssetTitle = (a) => {
    const props = a['edc:properties'] || a.properties || {};
    return props['http://purl.org/dc/terms/title'] || props['edc:name'] || props.name || a['@id'] || '';
  };

  // ── Search + pagination ──────────────────────────────────────────────────────
  const filteredOffers = useMemo(() => {
    if (!searchQuery.trim()) return offers;
    const q = searchQuery.toLowerCase();
    return offers.filter(o =>
      getOfferId(o).toLowerCase().includes(q) ||
      getAccessPolicy(o).toLowerCase().includes(q) ||
      getContractPolicy(o).toLowerCase().includes(q) ||
      getAssetSelector(o).toLowerCase().includes(q)
    );
  }, [offers, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredOffers.length / PAGE_SIZE));
  const pagedOffers = filteredOffers.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const statusIcon = { connected: <CheckCircle className="text-green-400" size={20} />, error: <AlertTriangle className="text-red-400" size={20} /> };
  const statusText = { connected: 'Connected', error: 'Connection Error' };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Data Offers</h1>
          <p className="text-gray-400">Publish data offers by linking assets and policies</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setShowConfig(!showConfig)} className="btn btn-secondary">
            <Settings size={20} />Configure Connector
          </button>
          {providerUrl && (
            <>
              <button onClick={openCreateModal} className="btn btn-primary">
                <Plus size={20} />Publish Data Offer
              </button>
              <button onClick={fetchOffers} className="btn btn-secondary" disabled={loading}>
                <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />Refresh
              </button>
            </>
          )}
        </div>
      </div>

      {/* Configuration Panel */}
      {showConfig && (
        <div className="card border-blue-500/30">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Settings size={20} className="text-blue-400" />Provider EDC Configuration
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Management API URL *</label>
              <input type="text" value={providerUrl} onChange={e => setProviderUrl(e.target.value)}
                
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">API Key (optional)</label>
              <div className="relative">
                <input type={showApiKey ? 'text' : 'password'} value={apiKey} onChange={e => setApiKey(e.target.value)}
                  placeholder=""
                  className="w-full px-4 py-2 pr-12 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                <button type="button" onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300">
                  {showApiKey ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={saveConfiguration} className="btn btn-primary"><Save size={20} />Save & Connect</button>
              <button onClick={() => setShowConfig(false)} className="btn btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* No connector warning */}
      {!providerUrl && !showConfig && (
        <div className="card border-yellow-500/30 bg-yellow-900/10">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-yellow-400 flex-shrink-0 mt-1" size={24} />
            <div>
              <h3 className="text-lg font-semibold text-yellow-400 mb-2">No Provider Connector Configured</h3>
              <p className="text-gray-300 mb-3">Configure your Provider EDC connector to manage data offers.</p>
              <button onClick={() => setShowConfig(true)} className="btn btn-primary"><Settings size={20} />Configure Now</button>
            </div>
          </div>
        </div>
      )}

      {/* API Status */}
      {providerUrl && (
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Provider EDC Connector</p>
              <p className="text-lg font-semibold mt-1">{statusText[apiStatus] || 'Checking…'}</p>
              {apiError && <p className="text-xs text-red-400 mt-1">{apiError}</p>}
            </div>
            {statusIcon[apiStatus] || <Clock className="text-yellow-400 animate-pulse" size={20} />}
          </div>
        </div>
      )}

      {/* Publish Data Offer Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card max-w-lg w-full mx-4 border-blue-500/30 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold flex items-center gap-2">
                <PackagePlus size={24} className="text-blue-400" />Publish New Data Offer
              </h3>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            {modalLoading && !policies.length && !assets.length ? (
              <div className="flex items-center justify-center py-12 gap-3 text-gray-400">
                <Loader2 className="animate-spin" size={24} />Loading policies and assets…
              </div>
            ) : (
              <div className="space-y-4">
                {/* ID */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">ID *</label>
                  <input type="text" value={newOffer.id}
                    onChange={e => setNewOffer(p => ({ ...p, id: e.target.value }))}
                    
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                </div>

                {/* Access Policy */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-1">
                    <Shield size={14} className="text-blue-400" />Access Policy *
                  </label>
                  {!modalLoading && policies.length === 0 ? (
                    <p className="text-sm text-yellow-400 italic">No policies found. Create a policy first.</p>
                  ) : (
                    <Combobox
                      options={policies.map(pol => ({ value: getPolicyId(pol), label: getPolicyId(pol) }))}
                      value={newOffer.accessPolicyId}
                      onChange={v => setNewOffer(p => ({ ...p, accessPolicyId: v }))}
                      placeholder="Search access policy…"
                      emptyText="No matching policies"
                    />
                  )}
                </div>

                {/* Contract Policy */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-1">
                    <Shield size={14} className="text-purple-400" />Contract Policy *
                  </label>
                  {!modalLoading && policies.length === 0 ? (
                    <p className="text-sm text-yellow-400 italic">No policies found. Create a policy first.</p>
                  ) : (
                    <Combobox
                      options={policies.map(pol => ({ value: getPolicyId(pol), label: getPolicyId(pol) }))}
                      value={newOffer.contractPolicyId}
                      onChange={v => setNewOffer(p => ({ ...p, contractPolicyId: v }))}
                      placeholder="Search contract policy…"
                      emptyText="No matching policies"
                    />
                  )}
                </div>

                {/* Asset */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-1">
                    <Database size={14} className="text-green-400" />Asset *
                  </label>
                  {!modalLoading && assets.length === 0 ? (
                    <p className="text-sm text-yellow-400 italic">No assets found. Create an asset first.</p>
                  ) : (
                    <Combobox
                      options={assets.map(a => {
                        const id = getAssetId(a);
                        const title = getAssetTitle(a);
                        return { value: id, label: title !== id ? title : id, sublabel: title !== id ? id : undefined };
                      })}
                      value={newOffer.assetId}
                      onChange={v => setNewOffer(p => ({ ...p, assetId: v }))}
                      placeholder="Search asset…"
                      emptyText="No matching assets"
                    />
                  )}
                </div>

                {modalError && (
                  <div className="bg-red-900/20 border border-red-800 rounded-lg p-3 flex gap-2">
                    <AlertCircle className="text-red-400 flex-shrink-0 mt-0.5" size={16} />
                    <p className="text-red-400 text-sm">{modalError}</p>
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button onClick={createOffer} disabled={modalLoading}
                    className="btn btn-primary flex-1">
                    {modalLoading ? <Loader2 className="animate-spin" size={18} /> : <PackagePlus size={18} />}
                    Create
                  </button>
                  <button onClick={() => setShowCreateModal(false)} className="btn btn-secondary flex-1">Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Data Offers List */}
      {providerUrl && offers.length > 0 && (
        <div className="card">
          {/* Header + search */}
          <div className="flex items-center justify-between mb-4 gap-4">
            <h2 className="text-xl font-semibold flex items-center gap-2 shrink-0">
              <FileSignature className="text-blue-400" size={24} />
              Data Offers ({filteredOffers.length}{filteredOffers.length !== offers.length ? ` / ${offers.length}` : ''})
            </h2>
            <div className="relative max-w-sm w-full">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search by ID, policy or asset…"
                className="w-full pl-9 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {filteredOffers.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-6">No data offers match your search.</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-700 text-gray-400 text-left">
                      <th className="pb-3 pr-4 font-medium">ID</th>
                      <th className="pb-3 pr-4 font-medium">Access Policy</th>
                      <th className="pb-3 pr-4 font-medium">Contract Policy</th>
                      <th className="pb-3 pr-4 font-medium">Asset</th>
                      <th className="pb-3 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {pagedOffers.map(offer => (
                      <tr key={getOfferId(offer)} className="hover:bg-gray-800/30 transition-colors group">
                        <td className="py-3 pr-4 font-mono text-xs text-white">{getOfferId(offer)}</td>
                        <td className="py-3 pr-4">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 offer-badge-blue rounded text-xs">
                            <Shield size={10} />{getAccessPolicy(offer)}
                          </span>
                        </td>
                        <td className="py-3 pr-4">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 offer-badge-purple rounded text-xs">
                            <Shield size={10} />{getContractPolicy(offer)}
                          </span>
                        </td>
                        <td className="py-3 pr-4">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 offer-badge-green rounded text-xs">
                            <Database size={10} />{getAssetSelector(offer)}
                          </span>
                        </td>
                        <td className="py-3 text-right">
                          <button onClick={() => deleteOffer(getOfferId(offer))}
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-red-400 hover:text-red-300" title="Delete offer">
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-700">
                  <p className="text-sm text-gray-400">
                    Showing {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, filteredOffers.length)} of {filteredOffers.length}
                  </p>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                      className="p-2 rounded-lg hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                      <ChevronLeft size={16} />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                      .reduce((acc, p, idx, arr) => {
                        if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                        acc.push(p);
                        return acc;
                      }, [])
                      .map((item, idx) =>
                        item === '…' ? (
                          <span key={`e-${idx}`} className="px-2 text-gray-500">…</span>
                        ) : (
                          <button key={item} onClick={() => setCurrentPage(item)}
                            className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${item === currentPage ? 'bg-blue-600 text-white' : 'hover:bg-gray-700 text-gray-300'}`}>
                            {item}
                          </button>
                        )
                      )}
                    <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                      className="p-2 rounded-lg hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Empty state */}
      {providerUrl && !loading && offers.length === 0 && apiStatus === 'connected' && (
        <div className="card text-center py-12">
          <FileSignature className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 mb-4">No data offers found. Publish your first data offer to make assets available to consumers.</p>
          <button onClick={openCreateModal} className="btn btn-primary mx-auto">
            <Plus size={20} />Publish Data Offer
          </button>
        </div>
      )}
    </div>
  );
}
